import { Client } from '../client';
import { DisplayNameType } from './display-name-type';
import { WidgetPosition } from './widget-position';
import { WidgetSettings } from './widget-settings';
import { WidgetState } from './widget-state';
import { WidgetTypes } from './widget-types';
import { FilterPreference } from '../filters/filter-preference';
import { Fund } from '../fund';
import { NamedQuery } from '../named-queries/named-query';
import { PermissionType } from '../shared/permission-type';
import { Sharing } from '../sharing/sharing';
import { SummaryConfig } from '../summary/summary-config';
import { SlicerConfigItem } from '../visualizations/config-item';
import { TableSortType } from '../visualizations/table-sort-type';
import { VisualizationConfigs } from '../visualizations/visualization-configs';
import { VisualizationType } from '../visualizations/visualization-type';

export interface NameValueChoice {
    label: string;
    value: string;
}

export class AppWidgetState extends WidgetState implements WidgetSettings {
    id?: number;
    name: string;
    description: string;
    coreWidgetId?: number;
    namedQuery?: NamedQuery;
    enableWidgetHeader?: boolean;
    visualizationConfigs: VisualizationConfigs[];
    currentVisualization?: VisualizationType;
    summaryConfig?: SummaryConfig;
    widgetPosition?: WidgetPosition;
    privilege?: PermissionType;
    isMaster?: boolean;
    isShared?: boolean;
    coreWidgetType?: WidgetTypes;
    customDisplayName?: string;
    displayNameType?: DisplayNameType;
    widgetSettings?: WidgetSettings;
    version: number;
    clientCode?: string;
    sharing: Sharing;
    // WidgetSettings
    isSubscribedToDashboardFilters: boolean;
    isActiveDate: boolean;
    tableSortDirection?: 'ASC' | 'DESC' | 'CUSTOM';
    sortTableBy?: TableSortType;
    widgetFilters?: FilterPreference;
    isGlobal?: boolean;
    isSelectable?: boolean;
    realtimeUpdates?: boolean;
    hideLoaderAfterFirstDataLoad?: boolean;
    isDetailWidget?: boolean;
    namedQueryId: string;
    clientCodes?: string[];

    // eslint-disable-next-line complexity
    constructor(init?: Partial<AppWidgetState>, funds?: Fund[], clients?: Client[]) {
        super();
        this.id = init?.id;
        this.name = init?.name ?? '';
        this.description = init?.description ?? '';
        this.coreWidgetId = init?.coreWidgetId;
        this.namedQuery = init?.namedQuery;
        this.enableWidgetHeader = init?.enableWidgetHeader;
        this.visualizationConfigs = init?.visualizationConfigs ?? [];
        this.currentVisualization = init?.currentVisualization ?? this.getCurrentVisualization();
        this.summaryConfig = init?.summaryConfig;
        this.widgetPosition = init?.widgetPosition;
        this.privilege = init?.privilege;
        this.isMaster = init?.isMaster;
        this.isShared = init?.isShared;
        this.coreWidgetType = init?.coreWidgetType;
        this.customDisplayName = init?.customDisplayName;
        this.displayNameType = init?.displayNameType;
        this.widgetSettings = init?.widgetSettings;
        this.version = init?.version ?? 0;
        this.clientCode = init?.clientCode;
        // Delete the below line when we fully migrate from MW/SQL
        this.isSubscribedToDashboardFilters = getIsSubscribedToDashboardFilters(init);
        // Uncomment the below line when we fully migrate from MW/SQL
        // this.isSubscribedToDashboardFilters = !!init?.widgetSettings?.isSubscribedToDashboardFilters;
        // Delete the below line when we fully migrate from MW/SQL
        this.isActiveDate = getIsActiveDate(init);
        // Uncomment the below line when we fully migrate from MW/SQL
        // this.isActiveDate = !!init?.widgetSettings?.isActiveDate;
        this.tableSortDirection = init?.tableSortDirection ?? this.getCurrentVisualizationTableSortDirection();
        this.sortTableBy = init?.sortTableBy ?? this.getCurrentVisualizationSortTableBy();
        this.widgetFilters = init?.widgetFilters;
        this.isGlobal = init?.isGlobal ?? !!init?.clientCodes?.length;
        this.isSelectable = init?.isSelectable;
        this.realtimeUpdates = init?.realtimeUpdates;
        this.hideLoaderAfterFirstDataLoad = init?.hideLoaderAfterFirstDataLoad;
        this.isDetailWidget = init?.isDetailWidget;
        this.namedQueryId = init?.namedQueryId ?? '';
        this.sharing = init?.sharing ?? { toUsers: [], toTeams: [] };

        this.coreWidgetId = this.coreWidgetId ?? this.id;
        this.visualizationConfigs = this.visualizationConfigs?.map((vizConfig: VisualizationConfigs) => {
            return new VisualizationConfigs(vizConfig);
        });

        if (funds && this.widgetFilters?.funds) {
            const wfFunds = this.widgetFilters.funds;
            this.widgetFilters.funds = funds.filter((f) => wfFunds.findIndex((fund) => fund.fundId === f.fundId) !== -1);
        }

        if (clients && this.widgetFilters) {
            this.widgetFilters.clients = clients;
        }
    }

    getDefaultVisualizationType(): VisualizationType | null {
        const defaultVisualizationConfig = this.defaultVisualizationConfig;
        return defaultVisualizationConfig ? defaultVisualizationConfig.visualizationType : null;
    }

    setDefaultVisualizationType(type: VisualizationType | undefined | null): void {
        if (!this.visualizationConfigs) {
            return;
        }

        this.visualizationConfigs.forEach((vc) => vc.isDefault = vc.visualizationType === type);
    }

    get configuredVisualizationTypes(): VisualizationType[] {
        return this.visualizationConfigs?.map((vc) => vc.visualizationType) ?? [];
    }

    getDefaultSlicerForVisualizationType(type: VisualizationType | undefined | null): string | undefined {
        const vc = this.getVisualizationConfigForType(type);
        if (!vc?.configs?.slicers) {
            return;
        }

        return vc.configs.slicers.find((slicer) => slicer.isDefault)?.value;
    }

    getDefaultSlicerObjectForVisualizationType(type: VisualizationType): Partial<SlicerConfigItem> | null {
        const vc = this.getVisualizationConfigForType(type);
        if (!vc?.configs?.slicers) {
            return null;
        }

        const defaultSlicer = vc.configs.slicers.find((slicer) => slicer.isDefault) ?? vc.configs.slicers[0];
        if (defaultSlicer) {
            return ({ label: defaultSlicer.label, value: defaultSlicer.value });
        }

        return null;
    }

    getSlicersForVisualizationType(type: VisualizationType): (string | undefined)[] {
        const vc = this.getVisualizationConfigForType(type);
        if (!vc?.configs?.slicers) {
            return [];
        }

        return vc.configs.slicers.map((slicer) => slicer.value);
    }

    getSortOnChoicesForVisualizationType(type: VisualizationType): NameValueChoice[] {
        const vc = this.getVisualizationConfigForType(type);
        if (!vc) {
            return [];
        }

        const sortOnChoices: NameValueChoice[] = [];
        if (['PIE_CHART',
            'DONUT_CHART',
            'HORIZONTAL_BAR_CHART',
            'VERTICAL_BAR_CHART',
            'HORIZONTAL_STACKED_BAR_CHART',
            'VERTICAL_STACKED_BAR_CHART',
            'VERTICAL_MIRRORED_BAR_CHART'].includes(type)) {
            sortOnChoices.push({ label: 'Slicer', value: 'SLICER' });

            if (type !== 'VERTICAL_MIRRORED_BAR_CHART') {
                sortOnChoices.push({ label: 'Value', value: 'VALUE' });
            }
        }

        return sortOnChoices;
    }

    getDefaultSortDirectionOptionsForDefaultVisualization(sortOn: string): NameValueChoice[] {
        const sortOptions = [
            { label: 'Asc', value: 'ASC' },
            { label: 'Desc', value: 'DESC' },
        ];
        if (sortOn !== 'VALUE') {
            const defaultSlicer = this.defaultVisualizationConfig?.configs?.slicers
                .find((slicer) => slicer.isDefault) ?? this.defaultVisualizationConfig?.configs?.slicers[0];
            if (defaultSlicer?.configCustomSortOrder?.length) {
                const customSortOption = { label: 'Custom', value: 'CUSTOM' };
                sortOptions.push(customSortOption);
            }
        }
        return sortOptions;
    }

    permissionForUser(username: string): string | undefined {
        const widgetEditRights = this.sharing.toUsers
            .some((user) => user.permission === 'write' && user.name.toLocaleLowerCase() === username.toLocaleLowerCase());

        return widgetEditRights ? 'EDIT' : this.privilege;
    }

    private getVisualizationConfigForType(type: VisualizationType | undefined | null): VisualizationConfigs | undefined {
        return this.visualizationConfigs?.find((vc) => vc.visualizationType === type);
    }

    private get defaultVisualizationConfig(): VisualizationConfigs | null {
        if (!this.visualizationConfigs) {
            return null;
        }

        return this.visualizationConfigs.find((vc) => vc.isDefault) ?? this.visualizationConfigs[0];
    }

    private getCurrentVisualization(): VisualizationType | undefined {
        return this.visualizationConfigs.find((vc) => vc.isDefault)?.visualizationType ??
            this.visualizationConfigs[0]?.visualizationType;
    }

    private getCurrentVisualizationTableSortDirection(): 'ASC' | 'DESC' | 'CUSTOM' | undefined {
        const currentVisualization = this.currentVisualization;
        if (!currentVisualization) {
            return undefined;
        }

        const vc = this.getVisualizationConfigForType(currentVisualization);
        if (!vc) {
            return undefined;
        }

        return vc.tableSortDirection;
    }

    private getCurrentVisualizationSortTableBy(): TableSortType | undefined {
        const currentVisualization = this.currentVisualization;
        if (!currentVisualization) {
            return undefined;
        }

        const vc = this.getVisualizationConfigForType(currentVisualization);
        if (!vc) {
            return undefined;
        }

        return vc.sortTableBy;
    }
}

export function anyWidgetIsRealtime(widgetStates: AppWidgetState[]): boolean {
    return widgetStates.some((w) => w.realtimeUpdates);
}

export function getCurrentVisualizationId(widgetPrefs: AppWidgetState | undefined | null): number | undefined {
    const currentVisualization = widgetPrefs?.currentVisualization;
    return widgetPrefs?.visualizationConfigs?.find((vc) => vc.visualizationType === currentVisualization)?.id;
}

// Delete this method after we fully migrate from MW/SQL
function getIsSubscribedToDashboardFilters(init?: Partial<AppWidgetState>): boolean {
    return init?.isSubscribedToDashboardFilters ?? init?.widgetSettings?.isSubscribedToDashboardFilters ?? false;
}

// Delete this method after we fully migrate from MW/SQL
function getIsActiveDate(init?: Partial<AppWidgetState>): boolean {
    return init?.isActiveDate ?? init?.widgetSettings?.isActiveDate ?? false;
}
